import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  TextField,
  ListItemText,
  List,
  IconButton,
  ListItem,
  CircularProgress,
  Paper,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Alert,
  DialogTitle,
} from "@mui/material";
import debounce from "lodash.debounce";

import SearchIcon from "@mui/icons-material/Search";
import Clientes from "../api/clientes";
import { Link } from "react-router-dom";
import LZString from "lz-string";
import MenuSection from "../components/MenuSection";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Percepciones from "../api/percepciones";
import apiErrorHandler from "../helpers/apiErrorHandler"; // Importa el helper

const ClientesSearchPage = () => {
  const esDUO = JSON.parse(localStorage.getItem("esDUO")) || false;
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [modalWidth, setModalWidth] = useState(window.innerWidth < 700 ? "360px" : "900px");
  const [isLoading, setIsLoading] = useState(false);
  const [allClientes, setAllClientes] = useState([]); // Mantiene TODOS los clientes iniciales
  const [showButtons, setShowButtons] = useState(false);
  const [showAllResults] = useState(false); // Estado para mostrar todos los resultados
  const navigate = useNavigate();
  const theme = useTheme();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  localStorage.removeItem("idcondicionVenta");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const clientes = await Clientes.getClientes("");
        console.log(clientes);
        const parsedClientes = Object.values(clientes.data.clientes); // Elimino el filtro activo ya que se hace en el endpoint
        setAllClientes(parsedClientes); // Guardar TODOS los clientes para siempre
        setSearchResults(parsedClientes);
        setShowButtons(true);
      } catch (error) {
        console.error("Error al obtener los datos:", error);
        apiErrorHandler(error, { stopFlow: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    const handleResize = () => {
      if (window.innerWidth < 700) {
        setModalWidth("360px");
      } else {
        setModalWidth("900px");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const handleConfirm = () => {
    setOpenConfirmDialog(false);
    navigate("/dashboard");
  };

  const handleCancel = () => {
    localStorage.setItem("cliente", "");
    navigate("/dashboard");
  };

  const handleSearch = useCallback(async () => {
    setIsLoading(true);
    try {
      const clientes = await Clientes.getClientes(searchTerm);
      console.log(clientes);
      const parsedClientes = Object.values(clientes.data.clientes); // Elimino el filtro activo ya que se hace en el endpoint
      setSearchResults(parsedClientes.slice(0, showAllResults ? parsedClientes.length : 50));
      setShowButtons(true);
    } catch (error) {
      console.error("Error al obtener los datos:", error);
      apiErrorHandler(error, { stopFlow: true });
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, showAllResults]);

  const debouncedSearch = useCallback(
    debounce(() => {
      handleSearch();
    }, 500), // Reduzco el tiempo de debounce para mejor responsividad
    [handleSearch],
  );

  useEffect(() => {
    if (searchTerm.trim() !== "") {
      debouncedSearch();
    } else {
      // Si el término está vacío, mostrar todos los clientes iniciales
      setSearchResults(allClientes.slice(0, showAllResults ? allClientes.length : 50));
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch, allClientes, showAllResults]);

  const handleRowClick = async cliente => {
    console.log("Cliente:", cliente); // Imprime el cliente completo
    console.log("Identificador:", cliente.identificador);
    if (cliente.condicionVenta.descripcionCondicionVenta == "CONTADO") {
      cliente.emiteRemito = false;
      cliente.emiteFacturayRemito = false;
    }
    const clienteStr = JSON.stringify(cliente); // Convierte el objeto a una cadena JSON
    localStorage.setItem("cliente", clienteStr);
    console.log("Cliente guardado en localStorage:", clienteStr);

    const numeroDocumento = cliente.numeroDocumento.replace(/-/g, "");
    console.log(numeroDocumento);
    setIsLoading(true);

    try {
      const percepciones = await Percepciones.getPercepciones(numeroDocumento);
      console.log(numeroDocumento);
      console.log(percepciones);
      localStorage.setItem("percepciones", percepciones);
      if (percepciones == true) {
        handleOpenConfirmDialog();
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error al obtener percepciones:", error);
      apiErrorHandler(error, { stopFlow: true });
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setSearchResults([]);
  };

  const handleCancelar = () => {
    setSearchTerm("");
    setSearchResults([]);
    localStorage.setItem("cliente", "");
    window.history.back();
  };

  useEffect(() => {
    // Asegura que se muestren los resultados basados en lo que hay en localStorage inmediatamente después de cargar.
    // No necesitas llamar a handleSearch aquí porque ya estás estableciendo los resultados basados en localStorage arriba.
    if (allClientes.length > 0) {
      setSearchResults(allClientes.slice(0, showAllResults ? allClientes.length : 50));
    }
  }, [allClientes, showAllResults]);
  return (
    <>
      <MenuSection />

      <Box
        sx={{
          position: "absolute",
          width: esDUO? 1100 : modalWidth,
          top: window.innerWidth < 700 ? "45%" : "54%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "white",
          p: 4,
          borderRadius: "8px",
          height: window.innerWidth < 700 ? "80vh" : "90%",
        }}
      >
        <TextField
          label="Razón social del cliente"
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
          }}
          onKeyDown={event => {
            if (event.key === "Enter") {
              handleSearch();
            }
          }}
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            endAdornment: (
              <IconButton onClick={handleSearch}>
                <SearchIcon sx={{ height: esDUO?"2.5rem":"2rem", width: esDUO?"2.5rem":"2rem" }} />
              </IconButton>
            ),
          }}
          InputLabelProps={{
            // Add this to style the input label
            style: { fontSize: esDUO?"1.2rem":"0.85rem", color: "gray" },
          }}
        />

        {isLoading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              paddingTop: "8rem",
              height: window.innerWidth < 700 ? 423 : 290,
            }}
          >
            <CircularProgress sx={{ color: theme.bandera.colorSecundario }} />
          </div>
        ) : (
          <>
            <Paper
              sx={{
                height: window.innerWidth < 700 ? 409 : esDUO?450:275,
                overflow: "auto",
                boxShadow: 0,
                marginBottom: 2,
              }}
            >
              {searchResults.map(
                (
                  cliente, // Cambia 'articulosArray' por 'searchResults' aquí
                ) => (
                  <List key={cliente.idCliente}>
                    <ListItem
                      key={cliente.idCliente}
                      sx={{
                        borderBottom: "1px solid #ccc",
                        display: "flex",
                        textDecoration: "none",
                        color: "inherit",
                      }}
                      component={Link}
                      onClick={() => {
                        handleRowClick(cliente);
                      }}
                    >
                      <ListItemText
                        primary={cliente.razonSocial}
                        secondary={cliente.identificador ? cliente.identificador : ""}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          fontFamily: 'Roboto, Arial, sans-serif',
                          WebkitFontSmoothing: 'antialiased',
                          MozOsxFontSmoothing: 'grayscale',
                          fontWeight: 400,
                          fontSize: esDUO?'1.5rem':'1rem',
                          letterSpacing: 0,
                          ['& .MuiTypography-root']: {
                            fontFamily: 'Roboto, Arial, sans-serif',
                            WebkitFontSmoothing: 'antialiased',
                            MozOsxFontSmoothing: 'grayscale',
                            fontWeight: 400,
                            fontSize: esDUO?'1.5rem':'1rem',
                            letterSpacing: 0,
                          },
                          ['& .MuiListItemText-secondary']: {
                            color: 'gray',
                            fontFamily: 'Roboto, Arial, sans-serif',
                            WebkitFontSmoothing: 'antialiased',
                            MozOsxFontSmoothing: 'grayscale',
                            fontWeight: 400,
                            fontSize: esDUO?'1.2rem':'0.95rem',
                            letterSpacing: 0,
                          },
                        }}
                      />
                    </ListItem>
                  </List>
                ),
              )}
            </Paper>
          </>
        )}
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: window.innerWidth < 700 ? "center" : "flex-end",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Button
                variant="contained"
                sx={{
                  boxShadow: 0,
                  paddingRight: window.innerWidth < 700 ? 2.5 : 1.5,
                  paddingLeft: window.innerWidth < 700 ? 2.5 : 1.5,
                  fontWeight: "bold",
                  fontSize: 17,
                  whiteSpace: "nowrap", // Aquí se aplica la propiedad nowrap
                  marginRight: window.innerWidth < 700 ? 5 : 0,
                  backgroundColor: theme.bandera.colorSecundario,
                  ":hover": {
                    backgroundColor: theme.bandera.colorSecundario,
                  },
                }}
                component={Link}
                to="/altacliente/"
              >
                ALTA CLIENTE
              </Button>
              <Button
                variant="contained"
                sx={{
                  marginLeft: "1rem",
                  boxShadow: 0,
                  fontWeight: "bold",
                  fontSize: 17,
                  backgroundColor: theme.bandera.colorSecundario,
                  ":hover": {
                    backgroundColor: theme.bandera.colorSecundario,
                  },
                }}
                onClick={handleCancelar}
              >
                CANCELAR
              </Button>
            </Box>
            <Dialog
              open={openConfirmDialog}
              onClose={handleCloseConfirmDialog}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
              sx={{
                "& .MuiDialog-paper": {
                  backgroundColor: theme.palette.background.default,
                  boxShadow: theme.shadows[5],
                  borderRadius: theme.shape.borderRadius,
                  display: "flex",
                  flexDirection: "column",
                },
              }}
            >
              <DialogTitle id="alert-dialog-title">{"Cliente con percepciones"}</DialogTitle>
              <DialogContent>
                <DialogContentText sx={{ fontSize: "1rem" }}>El cliente percibe percepciones ¿desea continuar?</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancel} color="primary">
                  <Typography variant="body1" style={{ fontSize: "16px", fontWeight: "bold" }}>
                    Cancelar
                  </Typography>
                </Button>

                <Button onClick={handleConfirm} color="primary" autoFocus>
                  <Typography variant="body1" style={{ fontSize: "16px", fontWeight: "bold" }}>
                    Aceptar
                  </Typography>
                </Button>
              </DialogActions>
            </Dialog>
          </>
        </div>
      </Box>
    </>
  );
};

export default ClientesSearchPage;
